import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { ErrorMessage, Field, Form, Formik } from "formik"

import { CountrySelect } from "components/general/CountrySelect"
import { Modal } from "components/modals/Modal"

import { useProps } from "contexts/PropsContext"

import { tenantOptions } from "constants/bonus"
import { formatRoleName } from "constants/roles"

import { RoleLookup, roleManagementService } from "services/role-management/role-management.service"
import { BUILTIN_ROLE_IDS } from "services/user/type"
import { userService } from "services/user/user.service"

import { adminCreateUserSchema } from "libs/form-schema"

const initialValues = {
  username: "",
  email: "",
  password: "",
  phoneNumber: "",
  country: "",
  tenantId: "",
  siteLang: "en",
  role: BUILTIN_ROLE_IDS.USER as string
}

type ModalCreateUserProps = {
  open: boolean
  handler: () => void
  onSuccess?: () => void
}

export const ModalCreateUser = ({ open, handler, onSuccess }: ModalCreateUserProps) => {
  const { user: appUser } = useProps()
  const [loading, setLoading] = useState(false)
  const [allRoles, setAllRoles] = useState<RoleLookup[]>([])

  useEffect(() => {
    if (!open) return
    roleManagementService
      .listRolesLookup()
      .then((rs) => rs && setAllRoles(rs))
      .catch(() => {
        // Non-fatal — fall back to BUILTIN_ROLE_IDS-only options below.
      })
  }, [open])

  // Prefer the dynamic list (built-in + custom). ADMIN is hidden from non-ADMIN
  // callers because the BE assertCanAssignRole subset check would reject it.
  const roleOptions = useMemo(() => {
    const isAdminCaller = appUser?.role === BUILTIN_ROLE_IDS.ADMIN
    const fromApi = allRoles
      .filter((r) => isAdminCaller || r.id !== BUILTIN_ROLE_IDS.ADMIN)
      .map((r) => ({
        value: r.id,
        label: r.is_builtin ? formatRoleName(r.id) : `${r.name} (custom)`
      }))
    if (fromApi.length > 0) return fromApi
    return Object.values(BUILTIN_ROLE_IDS)
      .filter((id) => isAdminCaller || id !== BUILTIN_ROLE_IDS.ADMIN)
      .map((id) => ({ value: id, label: formatRoleName(id) }))
  }, [allRoles, appUser?.role])

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      setLoading(true)
      await userService.createUser(values)
      toast.success("User created successfully")
      onSuccess?.()
      handler()
    } catch (err: any) {
      toast.error(err.data.message || "Failed to create user")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} handler={handler}>
      <div className="modal-box w-full max-w-2xl">
        <h3 className="text-lg font-semibold mb-4">Create New User</h3>

        <Formik initialValues={initialValues} validationSchema={adminCreateUserSchema} onSubmit={handleSubmit}>
          <Form className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="username">Username</label>
              <Field name="username" className="input input-bordered w-full" />
              <ErrorMessage name="username" component="div" className="text-red text-sm" />
            </div>

            <div>
              <label htmlFor="email">Email</label>
              <Field name="email" type="email" className="input input-bordered w-full" />
              <ErrorMessage name="email" component="div" className="text-red text-sm" />
            </div>

            <div>
              <label htmlFor="password">Password</label>
              <Field name="password" type="password" className="input input-bordered w-full" />
              <ErrorMessage name="password" component="div" className="text-red text-sm" />
            </div>

            <div>
              <label htmlFor="phoneNumber">Phone Number</label>
              <Field name="phoneNumber" className="input input-bordered w-full" />
              <ErrorMessage name="phoneNumber" component="div" className="text-red text-sm" />
            </div>

            <CountrySelect />

            <div>
              <label htmlFor="tenantId">Tenant</label>
              <Field as="select" name="tenantId" className="select select-bordered w-full">
                <option value="" disabled>
                  Select tenant
                </option>
                {tenantOptions.map((t) => (
                  <option key={t.name} value={t.value}>
                    {t.value}
                  </option>
                ))}
              </Field>
              <ErrorMessage name="tenantId" component="div" className="text-red text-sm" />
            </div>

            <div>
              <label htmlFor="role">Role</label>
              <Field as="select" name="role" className="select select-bordered w-full">
                {roleOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Field>
              <ErrorMessage name="role" component="div" className="text-red text-sm" />
            </div>

            <div className="col-span-full mt-4 text-right">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Creating..." : "Create User"}
              </button>
            </div>
          </Form>
        </Formik>
      </div>
    </Modal>
  )
}
